/**
 * Team Design — team-logo file upload binding.
 *
 * Wires the team-logo `<input type="file">` in main-collection-product-grid
 * to VisitorUpload (assets/visitor-upload.js, loaded globally in theme.liquid).
 *
 * On success:
 *  - stashes the upload result at window._teamLogoUpload (read by cart-add
 *    interceptors to attach the media ID as a line-item property)
 *  - persists the result via window.TeamDesignStorage so the logo survives
 *    refreshes and cross-collection navigation
 *  - dispatches 'teamLogoUploaded' so team-design.logo.js can overlay it
 *
 * On init, if a logo was previously persisted, restores the UI (preview
 * image, "Change" button label, remove button) so the controls match state.
 */
(function () {
  'use strict';

  var ERROR_AUTO_HIDE_MS = 4000;
  var statusHideTimer = null;

  function el(id) { return document.getElementById(id); }

  function setStatus(message, tone) {
    var statusEl = el('collection-team-upload-status');
    if (!statusEl) return;
    if (statusHideTimer) {
      clearTimeout(statusHideTimer);
      statusHideTimer = null;
    }
    statusEl.textContent = message || '';
    if (message) {
      statusEl.setAttribute('data-visible', 'true');
      statusEl.setAttribute('data-tone', tone || 'info');
      if (tone === 'error') {
        statusHideTimer = setTimeout(function () {
          statusEl.removeAttribute('data-visible');
          statusEl.textContent = '';
          statusHideTimer = null;
        }, ERROR_AUTO_HIDE_MS);
      }
    } else {
      statusEl.removeAttribute('data-visible');
      statusEl.removeAttribute('data-tone');
    }
  }

  function setEmptyState() {
    var btnLabel  = document.querySelector('.collection-team-upload__btn');
    var selected  = el('collection-team-upload-selected');
    var previewEl = el('collection-team-upload-preview');
    var thumb     = document.querySelector('.collection-team-upload__thumb');
    var filename  = el('collection-team-upload-filename');

    if (btnLabel) btnLabel.style.display = '';
    if (selected) selected.setAttribute('hidden', '');
    if (thumb)    thumb.classList.remove('collection-team-upload__thumb--has-image');
    if (previewEl) previewEl.src = '';
    if (filename) { filename.textContent = ''; filename.setAttribute('title', ''); }
  }

  function setFilledState(filenameText, previewUrl) {
    var btnLabel  = document.querySelector('.collection-team-upload__btn');
    var selected  = el('collection-team-upload-selected');
    var previewEl = el('collection-team-upload-preview');
    var thumb     = document.querySelector('.collection-team-upload__thumb');
    var filename  = el('collection-team-upload-filename');

    if (btnLabel) btnLabel.style.display = 'none';
    if (selected) selected.removeAttribute('hidden');
    if (previewEl && thumb) {
      if (previewUrl) {
        previewEl.src = previewUrl;
        thumb.classList.add('collection-team-upload__thumb--has-image');
      } else {
        previewEl.src = '';
        thumb.classList.remove('collection-team-upload__thumb--has-image');
      }
    }
    if (filename) {
      var label = filenameText || 'Logo';
      filename.textContent = label;
      filename.setAttribute('title', label);
    }
  }

  function reset() {
    window._teamLogoUpload = null;
    if (window.TeamDesignStorage) window.TeamDesignStorage.clearLogo();

    var input = el('collection-team-logo-input');
    if (input) input.value = '';

    setEmptyState();
    setStatus('');

    document.dispatchEvent(new CustomEvent('teamLogoRemoved'));
  }

  function init() {
    var input = el('collection-team-logo-input');
    if (!input) return;

    var btnText  = el('collection-team-upload-btn-text');
    var removeBtn = el('collection-team-upload-remove');
    var currentFileName = '';
    // Preserve the merchant-configured label so progress/error states can
    // restore it without hard-coding a string.
    var defaultBtnLabel = btnText ? btnText.textContent.trim() : 'Add Team Logo';

    // Restore persisted logo UI on page load / collection navigation.
    var saved = window.TeamDesignStorage && window.TeamDesignStorage.get();
    if (saved && saved.logo) {
      window._teamLogoUpload = saved.logo;
      var savedUrl = saved.logo.urls && (saved.logo.urls.thumbnail || saved.logo.urls.medium || saved.logo.urls.original);
      setFilledState(saved.logo.fileName, savedUrl);
    }

    // Arm the auto-load pipeline as soon as the shopper engages with the
    // logo upload control — focusing the hidden file input via keyboard
    // tab, or clicking anywhere in the upload wrapper (the `+ Upload Team
    // Logo` label, the existing-logo thumbnail row). Mirrors the team-
    // input focus handler in team-design.text.js so cookie-restored logo
    // state actually paints the cards after interaction. No-op once armed.
    function armOnInteract() {
      var ns = window.CollectionTeamText;
      if (ns && ns.arm) ns.arm();
    }
    input.addEventListener('focus', armOnInteract);
    var uploadWrap = el('collection-team-upload');
    if (uploadWrap) uploadWrap.addEventListener('click', armOnInteract);

    // Capture file name as soon as the user picks a file so we can show it
    // while the upload is in progress.
    input.addEventListener('change', function () {
      if (input.files && input.files[0]) {
        currentFileName = input.files[0].name;
      }
    });

    VisitorUpload.bindInput(input, {
      type: 'LOGO',
      accept: ['.png', '.jpg', '.jpeg', '.svg', '.pdf', '.ai', '.eps', '.webp'],
      maxFileSizeMb: 200,

      onStatus: function (status) {
        // Progress is already surfaced in the button label via onProgress;
        // the status row below is reserved for errors so we don't double up.
        // Still clear any lingering error message when a new flow starts.
        if (status === 'validating' || status === 'signing') {
          setStatus('');
        }
      },

      onProgress: function (percent) {
        if (!btnText) return;
        btnText.textContent = percent < 100 ? (percent + '%') : 'Processing…';
      },

      onSuccess: function (result) {
        if (!currentFileName && input.files && input.files[0]) {
          currentFileName = input.files[0].name;
        }
        result.fileName = currentFileName || result.fileName || 'Logo';

        window._teamLogoUpload = result;
        if (window.TeamDesignStorage) window.TeamDesignStorage.setLogo(result);

        var url = result.urls.thumbnail || result.urls.medium || result.urls.original;
        setFilledState(result.fileName, url);
        if (btnText) btnText.textContent = defaultBtnLabel;
        setStatus('');

        document.dispatchEvent(new CustomEvent('teamLogoUploaded', { detail: result }));
      },

      onError: function (error) {
        setStatus('Upload failed — please try again', 'error');
        if (btnText) btnText.textContent = defaultBtnLabel;
        currentFileName = '';
        console.error('[team-upload]', error.message);
      },
    });

    if (removeBtn) {
      removeBtn.addEventListener('click', reset);
    }
  }

  function waitForVisitorUpload() {
    if (window.VisitorUpload) {
      init();
    } else {
      setTimeout(waitForVisitorUpload, 100);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForVisitorUpload);
  } else {
    waitForVisitorUpload();
  }
})();
